import { formatReviewReason } from '@/modules/projects/labels'
import {
  CHECK_PAGES_UNREAD_REASON,
  type FlaggedCheckFile,
} from '@/modules/projects/status'

const NO_REASON_NOTE = 'Some checks in this file need review.'
const PLEASE_REVIEW = 'Please review.'

// Sorted page numbers as runs: [12, 13, 14, 83] reads "12-14", "83".
export const pageRanges = (sortedPages: number[]): string[] => {
  const ranges: string[] = []
  let runStart = sortedPages[0]
  sortedPages.forEach((page, index) => {
    const nextPage = sortedPages[index + 1]
    if (nextPage === page + 1) return
    ranges.push(runStart === page ? `${page}` : `${runStart}-${page}`)
    runStart = nextPage
  })
  return ranges
}

const joinWithAnd = (parts: string[]) =>
  parts.length < 2
    ? parts.join('')
    : `${parts.slice(0, -1).join(', ')} and ${parts[parts.length - 1]}`

const isEveryPage = ({ unreadPages, numberOfPages }: FlaggedCheckFile) =>
  numberOfPages > 1 && unreadPages.length === numberOfPages

// "Pages 12-40 and 83 could not be extracted.", or "All 149 pages ..." when none could be extracted.
export const unreadPagesSentence = (file: FlaggedCheckFile) => {
  if (isEveryPage(file))
    return `All ${file.numberOfPages} pages could not be extracted.`
  const ranges = pageRanges(file.unreadPages)
  const noun = file.unreadPages.length === 1 ? 'Page' : 'Pages'
  return `${noun} ${joinWithAnd(ranges)} could not be extracted.`
}

// The page sentence stands in for the unread-pages reason; every other reason keeps its label.
const noteSentences = (file: FlaggedCheckFile) => {
  if (file.unreadPages.length === 0) return file.reasons.map(formatReviewReason)
  const otherReasons = file.reasons.filter(
    (reason) => reason !== CHECK_PAGES_UNREAD_REASON,
  )
  return [unreadPagesSentence(file), ...otherReasons.map(formatReviewReason)]
}

export const flaggedCheckFileNote = (file: FlaggedCheckFile) => {
  const sentences = noteSentences(file)
  if (sentences.length === 0) return NO_REASON_NOTE
  return `${sentences.join(' ')} ${PLEASE_REVIEW}`
}
